import { useCallback } from 'react'

import { Button, Radio } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { useCompensationCycle } from 'modules/attendance/hooks/useCompensationCycle'
import {
  getCompensationPeriodDescription,
  getCompensationPeriods,
} from 'modules/attendance/lib/compensationPeriodUtils'
import { FormData } from 'modules/attendance/lib/forms/TimeBlocksSupplementForm'

export type PayrollPeriodOption = 'current' | 'next'

interface RadioOptionProps {
  value: PayrollPeriodOption
  selectedValue: string
  onChange: (value: string) => void
  label: string
  description: string
}

const PeriodOption: React.FC<RadioOptionProps> = ({
  value,
  selectedValue,
  onChange,
  label,
  description,
}) => (
  <Radio.Wrap>
    <Radio
      name={label}
      value={selectedValue}
      onChange={onChange}
      description={description}
      option={{ label, value }}
    />
  </Radio.Wrap>
)

export interface PayrollPeriodSelectionProps {
  form: FormikProps<FormData>
  onSubmit: () => void
  onCancel: () => void
  hasMissingCategoriesMappings: boolean
  isSubmitting?: boolean
}

const PayrollPeriodSelection: React.FC<PayrollPeriodSelectionProps> = ({
  form,
  onSubmit,
  onCancel,
  hasMissingCategoriesMappings,
  isSubmitting = false,
}) => {
  const { cycleRecurrence } = useCompensationCycle()
  const compensationPeriods = getCompensationPeriods(cycleRecurrence)

  const handlePeriodChange = useCallback(
    (value: string) => {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      form.setFieldValue('period', value as PayrollPeriodOption)
    },
    [form]
  )

  return (
    <Modal
      title={i18n.t('clock_in.sidepanel.compensation.modal.payroll_period.title')}
      description={i18n.t('clock_in.sidepanel.compensation.modal.payroll_period.description')}
      illustration="monety-bag"
      onSubmit={onSubmit}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} type="secondary" disabled={isSubmitting}>
            {i18n.t('common.cancel')}
          </Button>
          {hasMissingCategoriesMappings ? (
            <Button submit disabled={isSubmitting}>
              {i18n.t('common.continue')}
            </Button>
          ) : (
            <Button submit disabled={isSubmitting}>
              {i18n.t('time_tracking.modals.compensation.payroll.create.submit')}
            </Button>
          )}
        </>
      }
    >
      <PeriodOption
        value="current"
        selectedValue={form.values.period}
        onChange={handlePeriodChange}
        label={compensationPeriods.current.label}
        description={getCompensationPeriodDescription(true)}
      />
      <PeriodOption
        value="next"
        selectedValue={form.values.period}
        onChange={handlePeriodChange}
        label={compensationPeriods.next.label}
        description={getCompensationPeriodDescription(false)}
      />
    </Modal>
  )
}

export default PayrollPeriodSelection
